import { useEffect } from "react";

/**
 * useAutofillGuard — keep password managers and Safari contact autofill off
 * every field that is not a sign-in field.
 *
 * Staff spend the day typing CUSTOMERS' phones, emails and addresses into
 * Chariot, and the browser's helpers keep offering the staff member's own
 * identity for them (Bitwarden's inline menu, Safari's contact card, …).
 * Each manager honours an opt-out attribute, so this stamps all of them on
 * every text-like input/textarea/select the app renders, except:
 *
 *   - anything inside an element marked `data-autofill="allow"` (the login,
 *     2FA, register and reset-password forms), and
 *   - any field whose live type is `password` (a secret is exactly what a
 *     password manager is for).
 *
 * Mounted once at the app root. A MutationObserver covers fields React
 * renders later; a capture-phase focusin listener re-checks the focused
 * field so the attributes are there before the extension's own focus handler
 * reads them, whatever order the observers fired in. An explicit
 * autoComplete prop on a field is never overridden.
 *
 * Attributes: data-bwignore (Bitwarden), data-1p-ignore (1Password),
 * data-lpignore (LastPass), data-form-type="other" (Dashlane),
 * autocomplete="off" (Safari contact autofill; Chrome's address autofill
 * ignores it, that one is beyond a page's control).
 */
const FIELD_SELECTOR = "input, textarea, select";
const SKIP_TYPES = new Set(["hidden", "checkbox", "radio", "submit", "button", "reset", "file", "range", "color", "image"]);
const IGNORE_ATTRS = [
  ["data-bwignore", ""],
  ["data-1p-ignore", ""],
  ["data-lpignore", "true"],
  ["data-form-type", "other"],
];
const MARK = "data-autofill-guarded";

function guardField(el) {
  if (!(el instanceof Element) || el.hasAttribute(MARK)) return;
  if (SKIP_TYPES.has(el.type)) return;
  el.setAttribute(MARK, "");
  if (el.type === "password" || el.closest('[data-autofill="allow"]')) return;
  for (const [name, value] of IGNORE_ATTRS) {
    if (!el.hasAttribute(name)) el.setAttribute(name, value);
  }
  if (!el.hasAttribute("autocomplete")) el.setAttribute("autocomplete", "off");
}

function guardTree(root) {
  if (!(root instanceof Element)) return;
  if (root.matches(FIELD_SELECTOR)) guardField(root);
  root.querySelectorAll(FIELD_SELECTOR).forEach(guardField);
}

export default function useAutofillGuard() {
  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") return undefined;
    guardTree(document.body);
    const observer = new MutationObserver((records) => {
      for (const r of records) r.addedNodes.forEach(guardTree);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const onFocus = (e) => guardField(e.target);
    document.addEventListener("focusin", onFocus, true);
    return () => {
      observer.disconnect();
      document.removeEventListener("focusin", onFocus, true);
    };
  }, []);
}
